import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { useCreatePrescription } from '../../hooks/usePrescriptions';
import { usePrint } from '../../hooks/usePrint';
import { useVisit, useCreateVisit } from '../../hooks/useVisits';
import { PrescriptionBuilder } from '../../components/prescriptions/PrescriptionBuilder';
import { PatientSelector } from '../../components/patients/PatientSelector';
import { PageHeader } from '../../components/common/PageHeader';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { Button } from '../../components/common/Button';
import type { CreatePrescriptionDto } from '../../types/prescription.types';
import { printApi } from '../../api/print.api';
import toast from 'react-hot-toast';

export const PrescriptionBuilderPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Memoize visitId and patientId to prevent unnecessary re-renders
  const visitId = React.useMemo(() => {
    const id = searchParams.get('visitId');
    return id ? parseInt(id, 10) : 0;
  }, [searchParams]);
  
  const patientId = React.useMemo(() => {
    const id = searchParams.get('patientId');
    return id ? parseInt(id, 10) : 0;
  }, [searchParams]);

  // Only fetch visit if visitId is valid
  const { data: visit, isLoading: isLoadingVisit } = useVisit(visitId);
  const createVisitMutation = useCreateVisit();
  const createMutation = useCreatePrescription();
  const printMutation = usePrint();

  // Auto-create visit if patientId is provided but no visitId
  useEffect(() => {
    // Only create visit if we have patientId but no visitId, and we're not already creating or loading
    if (patientId > 0 && visitId === 0 && !createVisitMutation.isPending && !isLoadingVisit) {
      const createVisit = async () => {
        try {
          // Add a note to indicate this is for prescription only
          const prescriptionOnlyNote = t('visits.prescriptionOnlyVisit', 'زيارة للوصفة الطبية فقط') || 'زيارة للوصفة الطبية فقط';
          const trimmedNote = prescriptionOnlyNote.trim();
          if (!trimmedNote) {
            throw new Error('Translation for prescriptionOnlyVisit is missing or empty');
          }
          const newVisit = await createVisitMutation.mutateAsync({
            patientId,
            visitDate: new Date().toISOString().split('T')[0],
            diagnosis: undefined,
            notes: trimmedNote, // This note ensures the visit is not empty
            files: [],
          });
          navigate(`/prescriptions/new?visitId=${newVisit.id}`, { replace: true });
        } catch (error) {
          toast.error(t('visits.visitCreatedError', 'Failed to create visit. Please try again.'));
          console.error('Error creating visit:', error);
        }
      };
      createVisit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, visitId]); // Only depend on patientId and visitId to prevent unnecessary re-runs

  const handleCreate = async (data: CreatePrescriptionDto): Promise<number> => {
    try {
      const prescription = await createMutation.mutateAsync(data);
      toast.success(t('prescriptions.prescriptionCreated', 'تم إنشاء الوصفة الطبية بنجاح'));
      
      // Offer to download PDF after creation
      setTimeout(async () => {
        const shouldDownload = window.confirm(
          t('prescriptions.downloadPdfAfterCreate', 'هل تريد تحميل ملف PDF للوصفة الطبية؟')
        );
        if (shouldDownload) {
          try {
            await printApi.downloadPrescriptionPdf(prescription.id);
            toast.success(t('prescriptions.pdfDownloaded', 'تم تحميل ملف PDF بنجاح'));
          } catch (downloadError) {
            console.error('Error downloading PDF:', downloadError);
            toast.error(t('prescriptions.pdfDownloadError', 'فشل في تحميل ملف PDF'));
          }
        }
      }, 500);
      
      return prescription.id;
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { errors?: Record<string, string[]> } } };
      const errors = axiosError?.response?.data?.errors;
      let message = t('prescriptions.prescriptionCreatedError', 'فشل في إنشاء الوصفة الطبية. يرجى المحاولة مرة أخرى.');
      if (errors?.Items?.length) {
        const itemsMsg = errors.Items[0] ?? '';
        if (itemsMsg.toLowerCase().includes('medication') || itemsMsg.includes('دواء')) {
          message = t(
            'prescriptions.addMedicationAndDetails',
            'يجب إضافة دواء واحد على الأقل وتعبئة الجرعة والمدة بشكل صحيح ثم الضغط على «إضافة دواء».'
          );
        } else {
          message = itemsMsg;
        }
      } else if (errors && Object.keys(errors).length > 0) {
        const firstKey = Object.keys(errors)[0];
        const firstMsg = errors[firstKey]?.[0];
        if (firstMsg) message = firstMsg;
      }
      toast.error(message, { duration: 6000 });
      throw error;
    }
  };

  const handlePrint = async (prescriptionId: number): Promise<void> => {
    try {
      const response = await printMutation.mutateAsync(prescriptionId);
      // Check if response contains paper dimensions info
      const paperInfo = response?.paperWidthMM && response?.paperHeightMM
        ? ` (${response.paperWidthMM}mm × ${response.paperHeightMM}mm)`
        : '';
      toast.success(
        t('prescriptions.prescriptionPrinted', 'تم إرسال الوصفة الطبية للطابعة بنجاح') + paperInfo,
        {
          duration: 6000,
        }
      );
      
      // Show reminder about printer settings if paper dimensions are available
      if (response?.paperWidthMM && response?.paperHeightMM) {
        setTimeout(() => {
          toast(
            t(
              'prescriptions.printerSettingsReminder',
              `تأكد من أن إعدادات الطابعة تطابق حجم الورقة: ${response.paperWidthMM}mm × ${response.paperHeightMM}mm`
            ),
            {
              duration: 8000,
              icon: 'ℹ️',
            }
          );
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error printing prescription:', error);
      
      // Extract error message and check for PDF download option
      let errorMessage = t('prescriptions.printError', 'Failed to print prescription');
      let canDownloadPdf = false;
      let downloadUrl: string | undefined;
      
      if (error?.response?.data) {
        errorMessage = error.response.data.message || errorMessage;
        canDownloadPdf = error.response.data.canDownloadPdf === true;
        downloadUrl = error.response.data.downloadUrl;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Check if it's a virtual printer error
      const isVirtualPrinterError = errorMessage.includes('Virtual printer') || 
                                     errorMessage.includes('Microsoft Print to PDF') ||
                                     errorMessage.includes('Print to PDF') ||
                                     errorMessage.includes('SumatraPDF');
      
      // If virtual printer or canDownloadPdf, offer direct PDF download (avoids 0-byte files from Print to PDF dialog)
      if (isVirtualPrinterError || canDownloadPdf) {
        toast(
          t('prescriptions.virtualPrinterInfo', 'لحفظ الوصفة كـ PDF استخدم زر التحميل أدناه لتحصل على ملف صحيح.'),
          { duration: 5000, icon: 'ℹ️' }
        );

        setTimeout(async () => {
          const shouldDownload = window.confirm(
            t('prescriptions.downloadPdfPrompt', 'تحميل الوصفة كـ PDF الآن؟ (ملف كامل، ليس طباعة إلى PDF)')
          );
          if (shouldDownload) {
            try {
              // Use downloadUrl from response if available, otherwise construct it
              if (downloadUrl) {
                // Use the downloadUrl from the response
                const success = await printApi.downloadPrescriptionPdfByUrl(downloadUrl);
                if (success) {
                  toast.success(t('prescriptions.pdfDownloaded', 'تم تحميل ملف PDF بنجاح'));
                } else {
                  throw new Error('Download failed');
                }
              } else {
                // Fallback to using prescription ID
                await printApi.downloadPrescriptionPdf(prescriptionId);
                toast.success(t('prescriptions.pdfDownloaded', 'تم تحميل ملف PDF بنجاح'));
              }
            } catch (downloadError) {
              console.error('Error downloading PDF:', downloadError);
              toast.error(t('prescriptions.pdfDownloadError', 'فشل في تحميل ملف PDF'));
            }
          }
        }, 1000);
      } else {
        // Show regular error message
        toast.error(errorMessage, {
          duration: 8000,
        });
      }
    }
  };

  if (createVisitMutation.isPending) {
    return (
      <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
        <PageHeader title={t('prescriptions.createPrescription')} />
        <div className="d-flex justify-content-center align-items-center py-5">
          <LoadingSpinner size="lg" />
          <p className="ms-3">{t('prescriptions.creatingVisit')}</p>
        </div>
      </div>
    );
  }

  // If no visitId and no patientId, show patient selector (table + filter)
  if (!visitId && !patientId) {
    return (
      <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
        <PageHeader title={t('prescriptions.createPrescription')} />
        <div
          className="card rounded-3 overflow-hidden"
          style={{
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -1px rgba(0,0,0,0.04)',
            backgroundColor: '#f8fafc',
          }}
        >
          <div
            className="card-header py-3 px-4"
            style={{
              backgroundColor: '#f1f5f9',
              borderBottom: '2px solid #e2e8f0',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <h5 className="card-title mb-1 fw-bold text-dark">
              {t('prescriptions.selectPatient', 'اختر مريض')}
            </h5>
            <p className="text-muted small mb-0">
              {t('prescriptions.selectPatientDescription', 'يرجى اختيار مريض لإنشاء وصفة طبية جديدة. ابحث ثم اضغط إنشاء وصفة.')}
            </p>
          </div>
          <div
            className="card-body p-4"
            style={{
              backgroundColor: '#fff',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <PatientSelector
              onSelect={(patient) => {
                navigate(`/prescriptions/new?patientId=${patient.id}`);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingVisit) {
    return (
      <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
        <PageHeader title={t('prescriptions.createPrescription')} />
        <div className="d-flex justify-content-center align-items-center py-5">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (!visit) {
    return (
      <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
        <PageHeader title={t('prescriptions.createPrescription')} />
        <ErrorMessage message={t('prescriptions.visitNotFound')} />
      </div>
    );
  }

  return (
    <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        title={t('prescriptions.createPrescription')}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate(`/patients/${visit.patientId}`)}
          >
            {t('prescriptions.backToPatient')}
          </Button>
        }
      />

      <div className="row g-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-bottom">
              <h5 className="card-title mb-0 fw-bold">{t('prescriptions.visitInformation')}</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <p className="text-muted small mb-1">{t('prescriptions.patient')}</p>
                  <p className="mb-0 fw-semibold">{visit.patientName}</p>
                </div>
                {visit.diagnosis && (
                  <div className="col-12 col-md-6">
                    <p className="text-muted small mb-1">{t('prescriptions.diagnosis')}</p>
                    <p className="mb-0 fw-semibold">{visit.diagnosis}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
      </div>

        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-bottom">
              <h5 className="card-title mb-0 fw-bold">{t('prescriptions.createPrescription')}</h5>
            </div>
            <div className="card-body">
        <PrescriptionBuilder
          visitId={visitId}
                patientId={visit.patientId}
          onSubmit={handleCreate}
          onPrint={handlePrint}
                isLoading={createMutation.isPending || printMutation.isPending}
        />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

